/**
 * Semantic Integrity Dashboard
 * 
 * Read-only dashboard for semantic workspace health.
 * Displays concept health, drift, duplicate candidates, stale links,
 * unresolved reconciliation proposals, dormant systems, and recommended reviews.
 * 
 * Constraints:
 * - proofOnly=true
 * - executionAuthority=false
 * - advisoryOnly=true
 * - No mutation controls
 * - No automatic merge, rename, delete, admit, promote, resolve, auto-fix
 * - No closeout generation
 * - No memory rewriting
 * - No historical artifact rewriting
 * - Human review required
 */

import React, { useState } from 'react';
import {
  SemanticIntegrityDashboardModel,
  Severity,
  DashboardViewState
} from './types';
import { mockSemanticIntegrityDashboardModel } from './mockSemanticIntegrityData';
import { ConceptRelationshipGraphView } from './ConceptRelationshipGraphView';

interface SemanticIntegrityDashboardProps {
  data?: SemanticIntegrityDashboardModel;
}

export const SemanticIntegrityDashboard: React.FC<SemanticIntegrityDashboardProps> = ({
  data = mockSemanticIntegrityDashboardModel
}) => {
  const [viewState, setViewState] = useState<DashboardViewState>({
    selectedCategory: null,
    selectedSeverity: null,
    expandedFindings: new Set(),
    filterText: ''
  });
  const [activeTab, setActiveTab] = useState<'dashboard' | 'graph'>('dashboard');

  const toggleFindingExpansion = (findingId: string) => {
    const newExpanded = new Set(viewState.expandedFindings);
    if (newExpanded.has(findingId)) {
      newExpanded.delete(findingId);
    } else {
      newExpanded.add(findingId);
    }
    setViewState({ ...viewState, expandedFindings: newExpanded });
  };

  const selectCategory = (category: string | null) => {
    setViewState({ ...viewState, selectedCategory: category });
  };

  const selectSeverity = (severity: Severity | null) => {
    setViewState({ ...viewState, selectedSeverity: severity });
  };

  const getSeverityColor = (severity: Severity): string => {
    switch (severity) {
      case 'CRITICAL': return 'text-red-600 bg-red-50';
      case 'HIGH': return 'text-orange-600 bg-orange-50';
      case 'MEDIUM': return 'text-yellow-600 bg-yellow-50';
      case 'LOW': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const categoryColors: Record<string, string> = {
    conceptDrift: 'text-purple-600',
    duplicateCandidates: 'text-orange-600',
    unmappedTerminology: 'text-blue-600',
    staleDoctrineLinks: 'text-red-600',
    staleCloseoutLinks: 'text-pink-600',
    staleCapabilityLinks: 'text-indigo-600',
    staleMemoryLinks: 'text-cyan-600',
    lowConfidenceConcepts: 'text-yellow-600',
    unresolvedReconciliationProposals: 'text-amber-600',
    dormantConcepts: 'text-gray-600',
    dormantMemorySystems: 'text-slate-600',
    unresolvedGovernanceRecoveryItems: 'text-emerald-600'
  };

  return (
    <div className="semantic-integrity-dashboard p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Semantic Integrity Dashboard
        </h1>
        <p className="text-gray-600">
          Read-only view of semantic workspace health from maintenance receipt
        </p>
        <div className="mt-4 text-sm text-gray-500">
          <span className="font-mono">Receipt ID: {data.receiptId}</span>
          <span className="mx-2">|</span>
          <span className="font-mono">Generated: {new Date(data.generatedAt).toLocaleString()}</span>
          <span className="mx-2">|</span>
          <span className="font-mono">Version: {data.scanVersion}</span>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'dashboard'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Dashboard
          </button>
          <button
            onClick={() => setActiveTab('graph')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'graph'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Concept Relationship Graph
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'dashboard' ? (
        <>
      {/* Safety Boundaries */}
      <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h2 className="text-lg font-semibold text-blue-900 mb-3">Safety Boundaries</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 text-sm">
          <div className="flex items-center">
            <span className="text-green-600 mr-2">✓</span>
            <span className="text-blue-800">proofOnly: {data.boundary.proofOnly.toString()}</span>
          </div>
          <div className="flex items-center">
            <span className="text-green-600 mr-2">✓</span>
            <span className="text-blue-800">executionAuthority: {data.boundary.executionAuthority.toString()}</span>
          </div>
          <div className="flex items-center">
            <span className="text-green-600 mr-2">✓</span>
            <span className="text-blue-800">advisoryOnly: {data.boundary.advisoryOnly.toString()}</span>
          </div>
          <div className="flex items-center">
            <span className="text-green-600 mr-2">✓</span>
            <span className="text-blue-800">noAutomaticMerge: {data.boundary.noAutomaticMerge.toString()}</span>
          </div>
          <div className="flex items-center">
            <span className="text-green-600 mr-2">✓</span>
            <span className="text-blue-800">noAutomaticRename: {data.boundary.noAutomaticRename.toString()}</span>
          </div>
          <div className="flex items-center">
            <span className="text-green-600 mr-2">✓</span>
            <span className="text-blue-800">noAutomaticDelete: {data.boundary.noAutomaticDelete.toString()}</span>
          </div>
          <div className="flex items-center">
            <span className="text-green-600 mr-2">✓</span>
            <span className="text-blue-800">noAutomaticAdmission: {data.boundary.noAutomaticAdmission.toString()}</span>
          </div>
          <div className="flex items-center">
            <span className="text-green-600 mr-2">✓</span>
            <span className="text-blue-800">humanReviewRequired: {data.boundary.humanReviewRequired.toString()}</span>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Concept Health */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Concept Health</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Total Concepts</span>
              <span className="font-semibold text-gray-900">{data.conceptHealthSummary.totalConcepts}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">High Confidence</span>
              <span className="font-semibold text-green-600">{data.conceptHealthSummary.highConfidenceConcepts}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Medium Confidence</span>
              <span className="font-semibold text-yellow-600">{data.conceptHealthSummary.mediumConfidenceConcepts}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Low Confidence</span>
              <span className="font-semibold text-red-600">{data.conceptHealthSummary.lowConfidenceConcepts}</span>
            </div>
          </div>
        </div>

        {/* Findings Summary */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Findings Summary</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Total Findings</span>
              <span className="font-semibold text-gray-900">{data.findingsSummary.totalFindings}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">CRITICAL</span>
              <span className="font-semibold text-red-600">{data.findingsSummary.findingsBySeverity.CRITICAL}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">HIGH</span>
              <span className="font-semibold text-orange-600">{data.findingsSummary.findingsBySeverity.HIGH}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">MEDIUM</span>
              <span className="font-semibold text-yellow-600">{data.findingsSummary.findingsBySeverity.MEDIUM}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">LOW</span>
              <span className="font-semibold text-green-600">{data.findingsSummary.findingsBySeverity.LOW}</span>
            </div>
          </div>
        </div>

        {/* Reconciliation Summary */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Reconciliation</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Total Proposals</span>
              <span className="font-semibold text-gray-900">{data.reconciliationSummary.totalProposals}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Pending</span>
              <span className="font-semibold text-yellow-600">{data.reconciliationSummary.pendingProposals}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">In Review</span>
              <span className="font-semibold text-blue-600">{data.reconciliationSummary.inReviewProposals}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Resolved</span>
              <span className="font-semibold text-green-600">{data.reconciliationSummary.resolvedProposals}</span>
            </div>
          </div>
        </div>

        {/* Dormant Systems */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Dormant Systems</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Dormant Concepts</span>
              <span className="font-semibold text-gray-900">{data.dormantSystemSummary.dormantConcepts}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Dormant Memory</span>
              <span className="font-semibold text-gray-900">{data.dormantSystemSummary.dormantMemorySystems}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Dormant Capabilities</span>
              <span className="font-semibold text-gray-900">{data.dormantSystemSummary.dormantCapabilities}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Total Dormant</span>
              <span className="font-semibold text-gray-900">{data.dormantSystemSummary.totalDormantItems}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Findings by Category */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Findings by Category</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Object.entries(data.findingsSummary.findingsByCategory).map(([category, count]) => (
            <div
              key={category}
              className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                viewState.selectedCategory === category
                  ? 'bg-blue-50 border-blue-300'
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
              }`}
              onClick={() => selectCategory(viewState.selectedCategory === category ? null : category)}
            >
              <div className={`font-semibold ${categoryColors[category] || 'text-gray-900'}`}>
                {category.replace(/([A-Z])/g, ' $1').trim()}
              </div>
              <div className="text-2xl font-bold text-gray-900 mt-1">{count}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Drift Findings */}
      {data.driftFindings.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Concept Drift Findings</h2>
          <div className="space-y-4">
            {data.driftFindings.map((finding) => (
              <div
                key={finding.findingId}
                className="border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${getSeverityColor(finding.severity)}`}>
                      {finding.severity}
                    </span>
                    <span className="font-semibold text-gray-900">{finding.conceptLabel}</span>
                    <span className="text-gray-500 text-sm">{finding.conceptId}</span>
                  </div>
                  <button
                    onClick={() => toggleFindingExpansion(finding.findingId)}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    {viewState.expandedFindings.has(finding.findingId) ? 'Collapse' : 'View Details'}
                  </button>
                </div>
                {viewState.expandedFindings.has(finding.findingId) && (
                  <div className="mt-3 space-y-2 text-sm">
                    <div className="text-gray-600">{finding.description}</div>
                    <div className="text-gray-600">
                      <span className="font-semibold">Recommended Action:</span> {finding.recommendedAction}
                    </div>
                    <div className="text-gray-600">
                      <span className="font-semibold">Confidence:</span> {finding.confidence}
                    </div>
                    {finding.humanReviewRequired && (
                      <div className="text-yellow-600 font-semibold">
                        ⚠ Human Review Required
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Duplicate Candidates */}
      {data.duplicateCandidates.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Duplicate Candidates</h2>
          <div className="space-y-4">
            {data.duplicateCandidates.map((candidate) => (
              <div
                key={candidate.findingId}
                className="border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${getSeverityColor(candidate.severity)}`}>
                      {candidate.severity}
                    </span>
                    <span className="font-semibold text-gray-900">{candidate.conceptLabels.join(', ')}</span>
                  </div>
                  <button
                    onClick={() => toggleFindingExpansion(candidate.findingId)}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    {viewState.expandedFindings.has(candidate.findingId) ? 'Collapse' : 'View Details'}
                  </button>
                </div>
                {viewState.expandedFindings.has(candidate.findingId) && (
                  <div className="mt-3 space-y-2 text-sm">
                    <div className="text-gray-600">{candidate.description}</div>
                    <div className="text-gray-600">
                      <span className="font-semibold">Concept IDs:</span> {candidate.conceptIds.join(', ')}
                    </div>
                    <div className="text-gray-600">
                      <span className="font-semibold">Recommended Action:</span> {candidate.recommendedAction}
                    </div>
                    {candidate.humanReviewRequired && (
                      <div className="text-yellow-600 font-semibold">
                        ⚠ Human Review Required
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommended Reviews */}
      {data.recommendedReviews.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Recommended Reviews</h2>
          <div className="space-y-4">
            {data.recommendedReviews.map((review) => (
              <div
                key={review.reviewId}
                className="border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${getSeverityColor(review.priority)}`}>
                      {review.priority}
                    </span>
                    <span className="font-semibold text-gray-900">{review.reviewType.replace(/([A-Z])/g, ' $1').trim()}</span>
                    <span className="text-gray-500 text-sm">{review.estimatedEffort}</span>
                  </div>
                </div>
                <div className="mt-2 text-sm">
                  <div className="text-gray-600 mb-1">{review.rationale}</div>
                  <div className="text-gray-600">
                    <span className="font-semibold">Affected Concepts:</span> {review.affectedConceptLabels.join(', ')}
                  </div>
                  {review.humanReviewRequired && (
                    <div className="text-yellow-600 font-semibold mt-2">
                      ⚠ Human Review Required
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Metadata */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Scan Metadata</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Scan Duration:</span>
            <span className="ml-2 text-gray-900">{data.metadata.scanDuration}s</span>
          </div>
          <div>
            <span className="text-gray-600">Scan Trigger:</span>
            <span className="ml-2 text-gray-900">{data.metadata.scanTrigger}</span>
          </div>
          <div className="md:col-span-2">
            <span className="text-gray-600">Sources Scanned:</span>
            <div className="ml-2 mt-1 space-y-1">
              {data.metadata.sourcesScanned.map((source, index) => (
                <div key={index} className="text-gray-900 font-mono text-xs">
                  {source}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
        </>
      ) : (
        <ConceptRelationshipGraphView />
      )}
    </div>
  );
};

export default SemanticIntegrityDashboard;
